import {
  defaultPose,
  HEAD_R,
  HEAD_X,
  HEAD_Y,
  type EyePose,
  type PaperPose,
  type Pose,
} from "./pose";

export const EMOTIONS = [
  "neutral",
  "happy",
  "curious",
  "confused",
  "searching",
  "sad",
  "bored",
  "yawning",
  "sleeping",
  "out",
] as const;

export type Emotion = (typeof EMOTIONS)[number];

export const PAPERS_MODES = ["none", "messy", "orbiting", "neat"] as const;

export type Papers = (typeof PAPERS_MODES)[number];

const PAPER_W = 30;
const PAPER_H = 39;
const FLOOR_Y = 176;

function sheet(
  x: number,
  y: number,
  yaw: number,
  pitch: number,
  roll = 0,
): PaperPose {
  return { x, y, rotation: yaw, width: PAPER_W, height: PAPER_H, pitch, roll };
}

function papersFor(mode: Papers, paperOrbitT: number): PaperPose[] {
  if (mode === "none") return [];
  if (mode === "messy") {
    return [
      sheet(42, FLOOR_Y + 2, -30, 62),
      sheet(54, FLOOR_Y - 4, -10, 58),
      sheet(48, FLOOR_Y + 5, 10, 64),
      sheet(60, FLOOR_Y - 1, 26, 60, 6),
    ];
  }
  if (mode === "neat") {
    return [0, 1, 2].map((i) =>
      sheet(192 - 2.6 * i, FLOOR_Y - 3.4 * i, 10, 60),
    );
  }
  return [0, 1, 2, 3].map((i) => {
    const t = paperOrbitT;
    const ph = i * 1.67;
    return sheet(
      HEAD_X + 50 * Math.sin(t * 0.9 + ph) + 16 * Math.sin(t * 2.2 + ph * 2.1),
      HEAD_Y + 8 + 24 * Math.sin(t * 0.55 + ph * 0.7) + 11 * Math.cos(t * 1.55 + ph),
      28 * Math.sin(t * 1.15 + ph),
      42 * Math.sin(t * 0.92 + ph * 1.3),
      48 * Math.cos(t * 1.38 + ph * 0.55),
    );
  });
}

export function parseEmotion(value: string): Emotion {
  return (EMOTIONS as readonly string[]).includes(value) ? (value as Emotion) : "neutral";
}

export function parsePapers(value: string): Papers {
  return (PAPERS_MODES as readonly string[]).includes(value) ? (value as Papers) : "none";
}

function rotateAround(
  px: number,
  py: number,
  cx: number,
  cy: number,
  deg: number,
): { x: number; y: number } {
  const rad = (deg * Math.PI) / 180;
  const dx = px - cx;
  const dy = py - cy;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return {
    x: cx + dx * cos - dy * sin,
    y: cy + dx * sin + dy * cos,
  };
}

function bothEyes(partial: Partial<EyePose>): { eyeL: EyePose; eyeR: EyePose } {
  const rest = defaultPose();
  return {
    eyeL: { ...rest.eyeL, ...partial, x: partial.x ?? rest.eyeL.x },
    eyeR: { ...rest.eyeR, ...partial, x: partial.x !== undefined ? rest.eyeR.x + (partial.x - rest.eyeL.x) : rest.eyeR.x },
  };
}

function applyEmotion(pose: Pose, emotion: Emotion): void {
  const hangFloor = pose.tail2.y + pose.tail2.ry;
  switch (emotion) {
    case "neutral":
      pose.rotation = 0;
      Object.assign(pose, bothEyes({ kind: "dot" }));
      pose.mouth = { ...pose.mouth, kind: "smile", width: 8, curvature: 1.3, y: 2, bias: 0.6 };
      break;
    case "happy":
      pose.rotation = -8;
      Object.assign(pose, bothEyes({ kind: "dot", openness: 0.82 }));
      pose.mouth = { ...pose.mouth, kind: "smile", curvature: 5, width: 28.8, y: 2, bias: 3.2 };
      break;
    case "curious":
      pose.rotation = -18;
      Object.assign(pose, bothEyes({ kind: "dot", y: -9 }));
      pose.brow = 1;
      pose.browSide = 1;
      pose.mouth = { ...pose.mouth, kind: "o", width: 10, y: 2 };
      break;
    case "confused":
      pose.rotation = 12;
      Object.assign(pose, bothEyes({ kind: "dot", y: 1 }));
      pose.mouth = { ...pose.mouth, kind: "wavy", wave: 2.5, width: 19.2, y: 8 };
      break;
    case "searching":
      pose.rotation = 6;
      Object.assign(pose, bothEyes({ kind: "dot" }));
      pose.mouth = { ...pose.mouth, kind: "line", width: 10, y: 2 };
      break;
    case "sad":
      pose.rotation = 10;
      Object.assign(pose, bothEyes({ kind: "dot", y: 3 }));
      pose.mouth = { ...pose.mouth, kind: "frown", curvature: -4, width: 19.2, y: 8 };
      break;
    case "bored":
      pose.rotation = 16;
      pose.eyeL = { ...pose.eyeL, kind: "dot", openness: 0.48, y: 1 };
      pose.eyeR = { ...pose.eyeR, kind: "dot", openness: 0.28, y: 2 };
      pose.mouth = { ...pose.mouth, kind: "line", width: 7, y: 3 };
      break;
    case "yawning":
      pose.rotation = -12;
      Object.assign(pose, bothEyes({ kind: "dot", openness: 0.36, y: 1 }));
      pose.mouth = { ...pose.mouth, kind: "o", width: 16, y: 6 };
      break;
    case "sleeping":
      pose.rotation = 90;
      Object.assign(pose, bothEyes({ kind: "closed" }));
      pose.mouth = { ...pose.mouth, kind: "smile", curvature: 1.3, wave: 0, width: 8, y: 2, bias: 0.6 };
      break;
    case "out":
      pose.rotation = 90;
      Object.assign(pose, bothEyes({ kind: "cross" }));
      pose.mouth = { ...pose.mouth, kind: "wavy", wave: 2.2, width: 16, y: 4 };
      break;
  }

  const t1 = rotateAround(pose.tail1.x, pose.tail1.y, HEAD_X, HEAD_Y, pose.rotation);
  const t2 = rotateAround(pose.tail2.x, pose.tail2.y, HEAD_X, HEAD_Y, pose.rotation);
  pose.tail1 = { ...pose.tail1, ...t1 };
  pose.tail2 = { ...pose.tail2, ...t2 };

  if (emotion === "sleeping" || emotion === "out") {
    const floorY = hangFloor;
    pose.y = floorY - HEAD_R;
    pose.tail1 = { ...pose.tail1, y: floorY - pose.tail1.ry };
    pose.tail2 = { ...pose.tail2, y: floorY - pose.tail2.ry };
  }
}

export function targetPose(emotion: Emotion, papers: Papers, paperOrbitT: number): Pose {
  const pose = defaultPose();
  pose.paperOrbitT = paperOrbitT;
  applyEmotion(pose, emotion);
  pose.papers = papersFor(papers, paperOrbitT);
  return pose;
}
